import { http } from '@/interceptors/axiosRequestInterceptor';
import { ActivityItem, NavigationMenuItem, StatusActivity } from '@/models';
import { useMainStore } from '@/store';
import { ExclamationCircleIcon } from '@heroicons/vue/24/outline';
import { intersection } from 'lodash';
import { storeToRefs } from 'pinia';
import { h } from 'vue';
const iconRenderer = h(ExclamationCircleIcon, {
  class: 'size-5 text-[#B26A12]',
});
// Export the debounced search composable
export { useDebouncedSearch } from './useDebouncedSearch';

export const storage_url = import.meta.env.VITE_APP_FILE_URL;
export const custom_product_path_info = {
  name: 'Custom Design',
  url: 'placeholders/custom-design.png',
  extension: 'svg',
};
export const can = (permissionsArg: string | Array<string>, all = false): boolean => {
  const { permissions: userPermissions } = storeToRefs(useMainStore());
  const auth_permissions = userPermissions.value;
  if (typeof permissionsArg === 'string' && auth_permissions) {
    return auth_permissions.includes(permissionsArg);
  } else {
    const can_do = intersection(auth_permissions, permissionsArg);
    if (can_do) {
      if (all) {
        return can_do.length === permissionsArg.length;
      } else {
        return can_do.length > 0;
      }
    } else {
      return false;
    }
  }
};
export const openV2Application = (
  menuItem: NavigationMenuItem | null,
  newTab: boolean = false
): void => {
  if (menuItem) {
    const { to } = menuItem;
    const url = import.meta.env.VITE_VUE2_APP_URL + (to ?? '');
    if (newTab) {
      window.open(url, '_blank');
    } else {
      window.location.href = url;
    }
  }
};
export const isSuperOrOrderAdmin = (): boolean => {
  const { role } = storeToRefs(useMainStore());
  if (role.value) {
    return (
      role.value.name.toLowerCase() === 'orderadministrator' ||
      role.value.name.toLowerCase() === 'superadmin'
    );
  }
  return false;
};

export const timeAgo = (dateString: string): string => {
  const now = new Date();
  const date = new Date(dateString);
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  const years = Math.floor(diffInSeconds / (60 * 60 * 24 * 365));
  if (years > 0) {
    return years === 1 ? 'a year ago' : `${years} years ago`;
  }

  const months = Math.floor(diffInSeconds / (60 * 60 * 24 * 30));
  if (months > 0) {
    return months === 1 ? 'a month ago' : `${months} months ago`;
  }

  const days = Math.floor(diffInSeconds / (60 * 60 * 24));
  if (days > 0) {
    return days === 1 ? 'a day ago' : `${days} days ago`;
  }

  const hours = Math.floor(diffInSeconds / (60 * 60));
  if (hours > 0) {
    return hours === 1 ? 'an hour ago' : `${hours} hours ago`;
  }

  const minutes = Math.floor(diffInSeconds / 60);
  if (minutes > 0) {
    return minutes === 1 ? 'a minute ago' : `${minutes} minutes ago`;
  }

  return diffInSeconds === 1 ? 'a second ago' : `${diffInSeconds} seconds ago`;
};

type DateFormatOptions =
  | 'dd/MM/yyyy'
  | 'yyyy-MM-dd'
  | 'Month dd, yyyy'
  | 'ddth Month yyyy'
  | 'HH:mm Do MMM YY';

export const formatDate = (dateString: string, format?: DateFormatOptions): string => {
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.toLocaleString('default', { month: 'long' });
  const year = date.getFullYear();

  const suffix = (day: number): string => {
    if (day >= 11 && day <= 13) return 'th';
    switch (day % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  };

  const formats: { [key in DateFormatOptions]: string } = {
    'dd/MM/yyyy': `${day < 10 ? '0' + day : day}/${month}/${year}`,
    'yyyy-MM-dd': `${year}-${month.substring(0, 3)}-${day < 10 ? '0' + day : day}`,
    'Month dd, yyyy': `${month} ${day < 10 ? '0' + day : day}, ${year}`,
    'ddth Month yyyy': `${day}${suffix(day)} ${month} ${year}`,
    'HH:mm Do MMM YY': `${date.getHours()}:${date.getMinutes()} ${day}${suffix(day)} ${month.substring(0, 3)} ${year.toString().slice(-2)}`,
  };

  return format && formats[format] ? formats[format] : `${day}${suffix(day)} ${month} ${year}`;
};
export function getTimeFromISO(dateString) {
  const date = new Date(dateString)

  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')

  return `${hours}:${minutes}:${seconds}`
}
export function formatShortDate(dateStr: string) {
  const date = new Date(dateStr);
  const day = String(date.getDate()).padStart(2, '0');
  const month = date.toLocaleString('en-US', { month: 'short' }); // Jan, Feb, etc.
  const year = String(date.getFullYear()).slice(-2); // last two digits
  return `${day} ${month} ${year}`;
}

export const CustimooOrderFlowStatuses: any = {
  quote_requested: 'Quote Requested',
  quote_rejected: 'Quote Rejected',
  quote_provided: 'Quote Provided',
  submitted_for_factory_review: 'Submitted for Factory Review',
  order_cancel: 'Cancelled',
  order_approve: 'Mark to Factory',
  factory_approved: 'Factory Approved',
  factory_rejected: 'Factory Rejected',
  submitted_for_customer_review: 'Submitted for Customer Review',
  customer_approved: 'Customer Approved',
  customer_rejected: 'Customer Rejected',
  in_production: 'In Production',
  shipped: 'Shipped',
  completed: 'Completed',
  pending_for_factory_assignment: 'Pending For Factory Assignment',
  production_files_uploaded: 'In Production',
  FACTORYREVIEW: 'submitted_for_factory_review',
  ORDERAPPROVE: 'order_approve',
  ORDERCANCEL: 'order_cancel',
  FACTORYAPPROVED: 'factory_approved',
  FACTORYREJECTED: 'factory_rejected',
  CUSTOMERREVIEW: 'submitted_for_customer_review',
  CUSTOMERAPPROVED: 'customer_approved',
  CUSTOMERREJECTED: 'customer_rejected',
  ORDERINPRODUCTION: 'in_production',
  ORDERSHIPPED: 'shipped',
  ORDERCOMPLETED: 'completed',
  FACTORY_APPROVE_REJECT: 'factory_approve_reject',
  PRODUCTIONFILESUPLOADED: 'production_files_uploaded',
  QUALITYCONTROL: 'quality_control',
  on_hold: 'On Hold',
  delivery_request_date: 'Delivery Request'
};

export const activityStatus = {
  quote_requested: {
    title: 'Quote Requested',
    message: 'Quote requested.',
  },
  quote_updated: {
    title: 'Quote Updated',
    message: 'Quote updated by customer.',
  },
  quote_rejected: {
    title: 'Quote Rejected',
    message: 'Quote rejected.',
  },
  quote_provided: {
    title: 'Quote provided',
    message: 'Quote provided by merchant.',
  },
  submitted_for_factory_review: {
    title: 'Order Created',
    message: 'Your order has been created and is awaiting confirmation.',
  },
  order_approve: {
    title: 'Order Confirmed, Pending Artwork',
    message:
      'Your order has been forwarded to the factory. They will review the logos and design for accuracy and quality, making any necessary adjustments for optimal printing.',
  },
  factory_approved: {
    title: 'Artwork Approved, Pending Test Print',
    message:
      'The factory has approved your artwork. Expect a physical sample to be uploaded within 5-7 days.',
  },
  order_cancel: {
    title: 'Order Cancelled',
    message: 'You have cancelled the order.',
  },
  factory_rejected: {
    title: 'Artwork Rejected',
    message:
      'You rejected the Artwork. Please wait for the customer to edit and submit the artwork again.',
  },
  submitted_for_customer_review: {
    title: 'Design Sample Submitted',
    message:
      '<p>The factory has provided sample images. Once the test print(s) are approved, full production will begin.</p>\n' +
      '    <p><strong>Note:</strong> Any changes or comments before test print approval may delay production. Production starts after test print approval.</p>\n' +
      '    \n' +
      "    <ul class='list-disc pl-5 flex flex-col gap-2 w-full mb-1'>\n" +
      '        <li><strong>Sublimation Production Time:</strong> 3–4 weeks (including shipping)</li>\n' +
      '        <li><strong>Tackle Twill Production Time:</strong> 7–8 weeks (including shipping)</li>\n' +
      '    </ul>.',
  },
  customer_approved: {
    title: 'Design Sample Approved',
    message: 'Your sample has been approved. Production is now pending.',
  },
  customer_rejected: {
    title: 'Design Sample Rejected',
    message:
      'Customer has rejected the design samples. Please read the comments and upload new design samples.',
  },
  production_files_uploaded: {
    title: 'Production Files uploaded',
    message: 'Production Files uploaded',
  },
  in_production: {
    title: 'In Production',
    message: ` <p class='mb-2'>The factory has started manufacturing your products.</p>
                <ul class='list-disc pl-5 flex flex-col gap-2 w-full mb-1'>
                    <li ><strong>Sublimation Production Time:</strong> 3–4 weeks (including shipping)</li>
                    <li><strong>Tackle Twill Production Time:</strong> 7–8 weeks (including shipping)</li>
                </ul>
            <p class='bg-[#F3E3D0] flex gap-2 text-[#B26A12] p-1 rounded-md'><strong class="flex gap-2"> Important:</strong>  No further changes can be made at this stage.</p>`,
  },
  quality_control: {
    title: 'Quality Control',
    message: 'Quality Control',
  },
  shipped: {
    title: 'Order Shipped',
    message: 'You shipped the products.',
  },
  completed: {
    title: 'Order Completed',
    message: 'This order has completed successfully.',
  },
  on_hold: {
    title: 'Order Put on Hold',
    message: 'The order has been put on hold.',
  },
  resume_order: {
    title: 'Order Resumed',
    message: 'The order hold has been removed.',
  },
  delivery_request_date: {
    title: 'Delivery Request Date',
    message: 'The user has requested delivery.',
  }
};

export const getDateRange = (option: string): Record<'to' | 'from', Date> => {
  const today = new Date();
  const isoDay = today.getDay() === 0 ? 7 : today.getDay(); // Adjust for ISO standard (Monday to Sunday)

  switch (option) {
    case 'this_week': {
      const startOfWeek = new Date(Date.UTC(today.getFullYear(), today.getMonth(), 1, 0, 0, 0, 0));
      startOfWeek.setDate(today.getDate() - isoDay + 1); // Monday
      return {
        from: startOfWeek,
        to: today,
      };
    }
    case 'last_week': {
      const lastWeekEnd = new Date(today);
      lastWeekEnd.setDate(today.getDate() - isoDay); // Last Sunday
      const lastWeekStart = new Date(lastWeekEnd);
      lastWeekStart.setDate(lastWeekEnd.getDate() - 6); // Monday
      return {
        from: lastWeekStart,
        to: lastWeekEnd,
      };
    }
    // this month should be added after confirmation
    case 'this_month': {
      const startOfMonth = new Date(Date.UTC(today.getFullYear(), today.getMonth(), 1, 0, 0, 0, 0)); // First day of this month
      return {
        from: startOfMonth,
        to: today,
      };
    }
    case 'last_month': {
      const startOfLastMonth = new Date(
        Date.UTC(today.getFullYear(), today.getMonth() - 1, 1, 0, 0, 0, 0)
      );
      const endOfLastMonth = new Date(
        Date.UTC(today.getFullYear(), today.getMonth(), 0, 23, 59, 59, 999)
      ); // Last day of last month
      return {
        from: startOfLastMonth,
        to: endOfLastMonth,
      };
    }
    case 'this_quarter': {
      const currentQuarter = Math.floor(today.getUTCMonth() / 3) + 1;
      const startOfQuarter = new Date(
        Date.UTC(today.getUTCFullYear(), (currentQuarter - 1) * 3, 1)
      ); // First day of the quarter
      return {
        from: startOfQuarter,
        to: today,
      };
    }
    case 'last_quarter': {
      const currentQuarter = Math.floor(today.getUTCMonth() / 3) + 1;
      const startOfLastQuarter = new Date(
        Date.UTC(today.getUTCFullYear(), (currentQuarter - 2) * 3, 1)
      ); // First day of last quarter
      const endOfLastQuarter = new Date(
        Date.UTC(today.getUTCFullYear(), (currentQuarter - 1) * 3, 0, 23, 59, 59, 999)
      ); // Last day of last quarter

      if (currentQuarter === 1) {
        startOfLastQuarter.setUTCFullYear(today.getUTCFullYear() - 1);
        endOfLastQuarter.setUTCFullYear(today.getUTCFullYear() - 1);
      }

      return {
        from: startOfLastQuarter,

        to: endOfLastQuarter,
      };
    }
    case 'this_year': {
      return {
        from: new Date(Date.UTC(today.getUTCFullYear(), 0, 1)), // January 1
        to: today,
      };
    }
    case 'last_year': {
      const startOfLastYear = new Date(Date.UTC(today.getUTCFullYear() - 1, 0, 1)); // January 1 of last year
      const endOfLastYear = new Date(Date.UTC(today.getUTCFullYear() - 1, 11, 31, 23, 59, 59, 999)); // December 31 of last year
      return {
        from: startOfLastYear,
        to: endOfLastYear,
      };
    }
    default:
      throw new Error(`Unsupported option: ${option}`);
  }
};
export const cleanName = (name: any) => {
  if (!name) return "";               // null, undefined, empty
  if (typeof name !== "string") return "";

  const cleaned = name.replace(/null|null|null/gi, "").trim(); // remove "null" words

  return cleaned.length ? cleaned : "";
}
export function getFullCustomerName(first: any, last: any) {
  const f = first && first !== "null" ? first.trim() : "";
  const l = last && last !== "null" ? last.trim() : "";

  const full = `${f} ${l}`.trim().replace(/\s+/g, " "); // clean extra spaces

  return full.length ? full : "N/A";
}

export const checkIsEmpty = (obj: Object | Array<any>): boolean => {
  if (obj) {
    const obj_type = obj.constructor.name;
    if (obj_type === 'Array' && Array.isArray(obj)) {
      return obj.length === 0;
    } else if (obj_type === 'Object') {
      return Object.keys(obj).length === 0;
    } else {
      return true;
    }
  } else {
    return true;
  }
};

const getWindowObject = () => {
  try {
    return window.parent;
  } catch (error) {
    logError(error);
    return window;
  }
};
const getDomDocument = (parent_doc = false) => {
  if (parent_doc) {
    // return window.parent.document
    return getWindowObject().document;
  }
  const dom_document = document.querySelector(getWebComponentNames());
  return dom_document ? dom_document?.shadowRoot : document;
};
const getWebComponentNames = (return_string = true): any => {
  if (return_string) {
    return 'v-customizer, v-order-detail';
  } else {
    return ['v-customizer, v-order-detail'];
  }
};
export const downloadTemplate = async (prod_id: number, product_name: string): Promise<void> => {
  const { loading } = storeToRefs(useMainStore());
  loading.value = true;
  await http
    .get(`template/download/${prod_id}`, {
      responseType: 'blob',
    })
    .then((res) => {
      const blob = new Blob([res.data], {
        type: res.headers['content-type'],
      });
      const custom_document = getDomDocument(true);
      const link = custom_document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = 'product_' + product_name + '_template.xlsx';
      link.click();
    });
  loading.value = false;
};

const isDev = import.meta.env.DEV; // Vite-specific way to check development mode

export const logError = (...args: any[]): void => {
  console.error(...args);
};

// Calendar event and holiday type enums/constants
export enum CalendarEventType {
  Order = 'order',
  Holiday = 'holiday',
  Event = 'event',
}

export enum HolidayType {
  Public = 'public',
  Company = 'company',
  Personal = 'personal',
}

export enum OrderStatus {
  Pending = 'pending',
  Processing = 'processing',
  Completed = 'completed',
  Cancelled = 'cancelled',
  Delayed = 'delayed',
  Shipped = 'shipped',
}
export const getCurrentDatetime = (): string => {
  const now = new Date();
  return now.toISOString().replace(/:/g, '-').split('.')[0];
  // Formats to "YYYY-MM-DDTHH-MM-SS" (compatible for filenames)
};

export const urlToBase64 = (url: string) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.onload = function () {
      if (xhr.status === 200) {
        const reader = new FileReader();
        reader.onloadend = function () {
          resolve(reader.result);
        };
        reader.readAsDataURL(xhr.response);
      } else {
        reject(
          `Error (status = ${xhr.status}, status text = ${xhr.statusText}) while getting file from url ${xhr.responseURL}`
        );
      }
    };
    xhr.open('GET', url);
    xhr.responseType = 'blob';
    xhr.send();
  });
};

export const getFileExtensionType = (type: string, file_extension: string) => {
  const extensions: Record<any, any> = {
    raster: ['jpg', 'jpeg', 'png'],
    vector: ['svg', 'ai', 'eps', 'pdf'],
  };
  const type_extensions = extensions[type];
  if (file_extension) {
    const extension_index = file_extension.lastIndexOf('.');
    if (extension_index > 0) {
      file_extension = file_extension.substring(extension_index + 1);
    }
    return type_extensions.includes(file_extension);
  }
  return type_extensions;
};

export const findActivity = (
  status_activities: StatusActivity[],
  status: string,
  length: number
) => {
  const activities = status_activities.filter((activity_item) => activity_item.status === status);
  const activity = activities.find((activity_item) => {
    return activity_item.activity_items.length === length;
  });
  if (activity) {
    return activity;
  } else {
    return null;
  }
};
export const findActivityWithPosition = (
  activity_items: StatusActivity[],
  status: string,
  position: number
) => {
  const submittedItems = activity_items.filter((item) => item.status === status);
  // Return the second item if it exists, otherwise return the first
  if (submittedItems.length > position) {
    return submittedItems[position];
  } else if (submittedItems.length === 1) {
    return submittedItems[0];
  }
  return null; //
};

export const mergeActivityArray = (
  current_array,
  artwork_created_activity_items,
  submitted_customer_activity_review = null
) => {
  const existingFactoryIds = current_array.map((item) => item.factory_product_id);
  const submittedActivityFactoryIds = submitted_customer_activity_review.activity_items.map(
    (item) => item.factory_product_id
  );
  const current_array_data = JSON.parse(JSON.stringify(current_array));
  const artwork_created_activity_items_data = JSON.parse(
    JSON.stringify(artwork_created_activity_items)
  );

  const artwork_created_activity = artwork_created_activity_items_data.map(
    (item: Record<any, any>) => {
      let activity_item = item;
      if (
        submittedActivityFactoryIds.includes(item.factory_product_id) &&
        !existingFactoryIds.includes(item.factory_product_id)
      ) {
        const submitted_activity_item = submitted_customer_activity_review.activity_items.find(
          (activity_review_item: Record<any, any>) =>
            activity_review_item.factory_product_id === item.factory_product_id
        );
        activity_item.message = submitted_activity_item?.message;
        activity_item.skip_customer_approval = submitted_activity_item?.skip_customer_approval;
      } else if (existingFactoryIds.includes(item.factory_product_id)) {
        const current_array_item = current_array_data.find(
          (current_array_item: Record<any, any>) =>
            current_array_item.factory_product_id === item.factory_product_id
        );
        activity_item = JSON.parse(JSON.stringify(current_array_item));
      }
      return activity_item;
    }
  );

  return artwork_created_activity;
};

export const isBase64File = (str) => {
  return str && typeof str === 'string' && str.startsWith('data:') && str.includes('base64,');
};

export const base64ToFile = (base64String, isBase64String, fileName = '') => {
  let response_file: File | null = null;
  if (isBase64String) {
    const mime_type_matched = base64String.match(/^data:(.*);base64,/);
    const in_valid_base64_string = base64String.includes(',');
    // Ensure the base64 string is valid
    if (mime_type_matched && in_valid_base64_string) {
      const mimetype: string = mime_type_matched[1];
      const file_extension = getExtensionFromMimeType(mimetype);
      if (!fileName) {
        const date_time_formatted = getRandom(5) + '_' + getDateTimeFormatted();
        fileName = `${date_time_formatted}.${file_extension}`;
      }
      // Remove the prefix (if present)
      const base64Data = base64String.split(',')[1];

      // Decode the base64 string
      const byteString = atob(base64Data);

      // Create an array buffer
      const arrayBuffer = new ArrayBuffer(byteString.length);
      const intArray = new Uint8Array(arrayBuffer);

      for (let i = 0; i < byteString.length; i++) {
        intArray[i] = byteString.charCodeAt(i);
      }

      // Create a Blob and then a File
      const blob = new Blob([arrayBuffer], {
        type: 'application/octet-stream',
      });
      response_file = new File([blob], fileName, {
        type: 'application/octet-stream',
      });
    }
  } else {
    const blob = new Blob([base64String], {
      type: 'application/octet-stream',
    });
    response_file = new File([blob], fileName, {
      type: 'application/octet-stream',
    });
  }
  return response_file;
};
export const getExtensionFromMimeType = (mimetype: string) => {
  if (mimetype) {
    const mimetypes_info = {
      'image/jpeg': 'jpg',
      'image/png': 'png',
      'image/gif': 'gif',
      'image/bmp': 'bmp',
      'image/svg+xml': 'svg',
      'application/pdf': 'pdf',
    };
    return mimetypes_info[mimetype.toLowerCase()] || '';
  } else {
    return '';
  }
};

export const getRandom = (length = 5, type = 'number') => {
  let rand_string: number | string = '';
  if (type === 'number') {
    let max_number: number | string = '9';
    for (let i = 1; i < length; i++) {
      max_number = max_number + '9';
    }
    max_number = parseInt(max_number);
    rand_string = Math.floor(max_number + Math.random() * max_number);
  } else if (type === 'string') {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
    for (let i = 0; i < length; i++) {
      rand_string += chars.charAt(Math.floor(Math.random() * chars.length));
    }
  } else {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    for (let i = 0; i < length; i++) {
      rand_string += chars.charAt(Math.floor(Math.random() * chars.length));
    }
  }
  return rand_string;
};

export const getDateTimeFormatted = () => {
  const pad = (num, size) => num.toString().padStart(size, '0');
  const date = new Date();

  const day = pad(date.getDate(), 2);
  const month = pad(date.getMonth() + 1, 2); // Months are zero-indexed
  const year = date.getFullYear();
  const hours = pad(date.getHours() % 12 || 12, 2); // 12-hour format
  const minutes = pad(date.getMinutes(), 2);
  const seconds = pad(date.getSeconds(), 2);
  const milliseconds = pad(date.getMilliseconds(), 3); // Milliseconds
  // Convert milliseconds to microseconds (approximation)
  const microseconds = pad(milliseconds * 1000, 6);
  return `${day}${month}${year}${hours}${minutes}${seconds}${microseconds}`;
};

export const getFileExtension = (url) => {
  if (!url) return '';
  return url.split('.').pop().toLowerCase();
};

export const isNullOrEmpty = (value: any): boolean => {
  return value === null || value === undefined || value === '' || value === 'null';
};

export const getAwsURLFromOrder = async (order_id: number): Promise<void> => {
  const { loading } = storeToRefs(useMainStore());
  loading.value = true;
  await http.get(`designfile/order/${order_id}`).then((res) => {
    const url = res.data.result.url;
    if (url) {
      window.open(`${url}`, '_blank');
    }
    // const blob = new Blob([res.data], {
    //     type: res.headers["content-type"],
    // });
    // const custom_document = getDomDocument(true);
    // const link = custom_document.createElement("a");
    // link.href = window.URL.createObjectURL(blob);
    // link.download = "product_" + product_name + "_template.xlsx";
    // link.click();
  });
  loading.value = false;
};


function deepEqual(obj1: any, obj2: any) {
  // If both are strictly equal (covers primitives and same references)
  if (obj1 === obj2) return true;

  // If either is not an object or is null
  if (typeof obj1 !== "object" || obj1 === null ||
      typeof obj2 !== "object" || obj2 === null) {
    return false;
  }

  // Compare keys length
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  if (keys1.length !== keys2.length) return false;

  // Compare each key/value
  for (let key of keys1) {
    if (!keys2.includes(key)) return false;
    if (!deepEqual(obj1[key], obj2[key])) return false;
  }

  return true;
}
export async function downloadFile(url: string, filename: string) {

  window.open(`${url}?q=${Math.random()}`, '_blank');
  // const res = await fetch(url);
  // const blob = await res.blob();
  // const link = document.createElement("a");
  // link.href = URL.createObjectURL(blob);
  // link.download = filename;
  // document.body.appendChild(link);
  // link.click();
  // document.body.removeChild(link);
  // URL.revokeObjectURL(link.href);
}

export const setPageTitle = (title= ''): void => {
  const domDocument = window.parent.document;
  domDocument.title = import.meta.env.VITE_APP_NAME + ` ${title}`;
}
export const allowNumberInput1 = (e: Event,  decimalPlaces: number = 2) => {
  const input = e.target as HTMLInputElement
  let value = input.value

  // Remove everything except digits and dot
  value = value.replace(/[^0-9.]/g, '')

  // Split integer and decimal
  const [intPart, decPart] = value.split('.')

  if (decPart !== undefined) {
    // Limit decimals to given decimalPlaces
    value = intPart + '.' + decPart.slice(0, decimalPlaces)
  }
  // Update input value (v-model will sync automatically)
  input.value = value
}
export const formatToNumber = (
  item: Record<string, any> | string | number,
  key?: string,
  maxDecimals: number = 3
): string => {
  let value: string;

  if (typeof item === 'object' && key) {
    value = String(item[key] ?? '');
  } else {
    value = String(item ?? '');
  }

  // Remove everything except digits and dot
  value = value.replace(/[^0-9.]/g, '');

  // Keep only first dot
  const firstDotIndex = value.indexOf('.');
  if (firstDotIndex !== -1) {
    value =
      value.slice(0, firstDotIndex + 1) +
      value.slice(firstDotIndex + 1).replace(/\./g, '');
  }

  // Split into integer and decimal parts
  let [intPart, decPart] = value.split('.');
  decPart = decPart ? decPart.slice(0, maxDecimals) : '';

  // Apply decimal rules
  if (decPart === '' || /^0+$/.test(decPart)) {
    value = intPart; // all decimals zero → remove
  } else if (decPart.length === 3 && decPart[2] === '0') {
    value = `${intPart}.${decPart.slice(0, 2)}`; // 3rd decimal zero → keep first 2
  } else if (decPart.length === 1 || decPart.length === 2) {
    value = `${intPart}.${decPart.padEnd(2, '0')}`; // force 2 decimals if any non-zero
  } else {
    value = `${intPart}.${decPart}`; // all 3 decimals meaningful → keep all
  }

  // Update object if passed
  if (typeof item === 'object' && key) {
    item[key] = value;
  }

  return value;
};

